import type React from "react";
import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { FiLogOut } from "react-icons/fi";
import { useAuth } from "../../lib/auth";
import { listGalleryImages } from "../../lib/gallery";
import type { GalleryImage } from "../../lib/gallery";
import { Divider } from "../Ornament";
import { easeManor } from "../../constants/constants";
import SignIn from "./SignIn";
import StatTile from "./StatTile";
import GalleryManager from "./GalleryManager";
import AnalyticsPanel from "./AnalyticsPanel";
import { trackEvent } from "../../lib/analytics";

const formatDate = (iso: string | null): string => {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
};

const AdminPage: React.FC = () => {
  const { loading, user, isAdmin, signOut } = useAuth();
  const [images, setImages] = useState<GalleryImage[]>([]);
  const [fetchState, setFetchState] = useState<"idle" | "loading" | "ready" | "error">(
    "idle",
  );
  const [fetchError, setFetchError] = useState<string | null>(null);

  useEffect(() => {
    if (!isAdmin) return;
    trackEvent("admin_signed_in");
    setFetchState("loading");
    listGalleryImages()
      .then((imgs) => {
        setImages(imgs);
        setFetchState("ready");
      })
      .catch((err) => {
        setFetchError(err instanceof Error ? err.message : "Failed to load gallery");
        setFetchState("error");
      });
  }, [isAdmin]);

  const lastUpload = useMemo(() => {
    if (!images.length) return null;
    return images.reduce<string | null>(
      (acc, i) => (acc && acc > i.createdAt ? acc : i.createdAt),
      null,
    );
  }, [images]);

  if (loading) {
    return (
      <div className="pt-28 pb-24 min-h-[60vh] flex items-center justify-center px-6">
        <motion.p
          className="u-label"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, ease: easeManor, delay: 0.4 }}
        >
          Preparing the drawing room…
        </motion.p>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="pt-28 pb-24 px-6 flex items-center justify-center min-h-[70vh]">
        <SignIn />
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="pt-28 pb-24 px-6 flex items-center justify-center min-h-[70vh]">
        <SignIn notAdmin email={user.email ?? null} />
      </div>
    );
  }

  return (
    <div className="pt-28 pb-24 px-6">
      <motion.header
        initial={{ opacity: 0, y: 14 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.9, ease: easeManor }}
        className="max-w-6xl mx-auto flex items-end justify-between gap-6 flex-wrap"
      >
        <div>
          <p className="u-label">The Proprietor's Study</p>
          <h1 className="mt-3 font-display text-[clamp(2.4rem,6vw,4.25rem)] leading-[0.98]">
            Welcome back, <em>{user.user_metadata?.full_name?.split(" ")[0] ?? "friend"}</em>.
          </h1>
          <p className="mt-2 italic text-[color:var(--ink-soft)]">
            Signed in as {user.email}
          </p>
        </div>
        <button type="button" onClick={signOut} className="btn-ghost">
          <FiLogOut size={14} />
          <span>Sign out</span>
        </button>
      </motion.header>

      <div className="max-w-6xl mx-auto mt-8">
        <Divider />
      </div>

      <section className="max-w-6xl mx-auto mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
        <StatTile
          label="Photographs in gallery"
          value={fetchState === "ready" ? String(images.length) : "—"}
          hint={fetchState === "loading" ? "gathering…" : undefined}
          delay={0}
        />
        <StatTile
          label="Last photograph added"
          value={formatDate(lastUpload)}
          hint={lastUpload ? "most recent upload" : "none yet"}
          delay={0.08}
        />
        <StatTile
          label="Proprietors on the allowlist"
          value="4"
          hint="edit in src/lib/allowlist.ts"
          delay={0.16}
        />
      </section>

      <section className="max-w-6xl mx-auto mt-20">
        <Divider />
        <div className="mt-14">
          {fetchState === "error" ? (
            <div className="card-parchment px-8 py-10 text-center">
              <p className="u-label text-[color:var(--claret)]">Could not load the gallery</p>
              <p className="mt-3 italic text-[color:var(--ink-soft)]">{fetchError}</p>
              <p className="mt-6 text-sm text-[color:var(--ink-soft)]">
                Confirm your Supabase project has the <code>gallery_images</code> table
                and the <code>gallery</code> storage bucket. See SETUP.md.
              </p>
            </div>
          ) : (
            <GalleryManager images={images} onChange={setImages} />
          )}
        </div>
      </section>

      <section className="max-w-6xl mx-auto mt-20">
        <Divider />
        <div className="mt-14">
          <AnalyticsPanel />
        </div>
      </section>
    </div>
  );
};

export default AdminPage;
